<template>
    <Loading :loading="loading" class="min-h-screen w-full max-w-[1250px] bg-[#f1f6fa] px-6 lg:px-20 lg:py-10 flex flex-col gap-6">
        <div>
            <!--Path navigation start-->
            <Path>
                <router-link to="/dashboard" class="text-blue-500">Dashboard</router-link>
                <i class="flex fi fi-rr-angle-small-right"></i>
                <router-link to="/dashboard/markets" class="text-blue-500">Analysen</router-link>
                <template v-if="analysis.product">
                    <i class="flex fi fi-rr-angle-small-right"></i>
                    <router-link v-if="analysis.product" :to="createPreviousURL()" class="text-blue-500 cursor-pointer">{{ analysisName }}</router-link>
                </template>
                <template v-if="analysis.published_at">
                    <i class="flex fi fi-rr-angle-small-right"></i>
                    <p class="text-blue-500">{{ formatDateByRegion($moment(analysis.published_at)) }}</p>
                </template>
            </Path>
            <!--Path navigation ends-->
        </div>
        <div :class="['min-h-screen w-full bg-[#f1f6fa] flex pt-4 flex-col gap-6', images.length > 0 ? 'xl:flex-row' : 'xl:flex-col']">
            <!--Fullscreen -->
            <div v-if="fullscreen" ref="image_preview" id="draggableArea" class="fixed top-0 bottom-0 left-0 right-0 z-50 overflow-hidden bg-white">
                <div class="relative">
                    <div @click="toggleFullscreen()" class="z-30 h-8 flex items-center text-gray-900 absolute top-[1rem] left-[1rem] cursor-pointer">
                        <span class="font-light">{{ $t('Back') }}</span>
                    </div>
                    <div
                        @click="toggleFullscreen()"
                        class="z-30 w-8 h-8 flex items-center justify-center rounded-xl bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[1rem] cursor-pointer"
                    >
                        <i class="fi fi-ss-cross-small w-8 h-8 flex items-center justify-center rounded-xl"></i>
                    </div>
                    <div
                        @click="$refs.zoomer.zoomIn((scale = 1.5))"
                        v-if="fullscreen"
                        class="z-30 w-8 h-8 flex items-center justify-center rounded-xl bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[3.5rem] cursor-pointer"
                    >
                        <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                        <span class="font-light">+</span>
                    </div>
                    <div
                        @click="$refs.zoomer.zoomOut((scale = 0.5))"
                        v-if="fullscreen"
                        class="z-30 w-8 h-8 flex items-center justify-center rounded-xl bg-[#CCCCCC] text-gray-900 absolute top-[1rem] right-[6rem] cursor-pointer"
                    >
                        <Icon type="Search" viewBox="0 0 19 20" width="12" height="12" class="fill-current" />
                        <span class="font-light">-</span>
                    </div>
                    <v-zoomer ref="zoomer" :max-scale="10" :zooming-elastic="false" class="h-screen">
                        <img @contextmenu.prevent class="object-contain w-full h-full prevent-click" :src="fullscreenImage" />
                    </v-zoomer>

                    <!-- Image navigation for small screens -->
                    <div v-show="fullscreen" class="absolute bottom-[1rem] left-0 right-0 z-50">
                        <div class="flex max-w-full gap-2 p-2 mx-auto overflow-x-auto bg-white rounded-md shadow-md w-fit overflow-y-clip">
                            <span
                                @click="[slideToImage(index)]"
                                v-for="(item, index) in images"
                                :key="item.id"
                                :class="index === activeSliderIndex ? 'bg-blue text-white' : 'text-blue'"
                                class="p-2 border rounded-md shadow-md cursor-pointer border-blue whitespace-nowrap"
                            >
                                {{ $translate(item.attributes.title) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <!--END-->
            <div ref="contentContainer" class="flex flex-col w-full gap-8 mx-auto">
                <div class="flex flex-col gap-6 p-6 bg-white rounded-xl">
                    <div class="flex flex-row items-center gap-4">
                        <img v-if="analysis.product && productImage" class="object-contain w-auto h-12 duration-200" :src="productImage.replace('.png', '-gradient.png')" />
                        <i v-if="!analysis.product" class="flex text-6xl fi fi-tr-newspaper"></i>
                        <div>
                            <p class="font-bold text-md">{{ analysisName }}</p>
                            <p class="pr-4 text-sm leading-4 text-dashboard-primary-grey line-clamp-3" v-html="productGroupShortDescription.de" />
                        </div>
                        <span
                            v-if="analysis.product"
                            class="bg-[#F1F6FA] border-0 rounded-xl p-2 ml-auto hover:scale-105 hover:shadow-md duration-300"
                            @click="toggleFavoriteReadStatus(analysis, true)"
                        >
                            <i v-if="!favoriteStatus" class="flex fi fi-rr-bookmark"></i>
                            <i v-else class="flex fi fi-sr-bookmark"></i>
                        </span>
                    </div>
                </div>

                <div class="flex flex-col">
                    <!-- Image Slider -->
                    <div v-if="images.length" class="p-4 bg-white rounded-t-xl">
                        <div class="relative">
                            <!-- Image Slider -->
                            <div class="overflow-x-hidden image-slider">
                                <div class="relative swiper-wrapper">
                                    <div v-for="item in images" :key="item.id" class="swiper-slide">
                                        <div
                                            @click="toggleFullscreen(item.attributes.image)"
                                            class="z-30 w-8 h-8 flex items-center justify-center rounded-xl bg-[#CCCCCC] text-gray-900 absolute top-4 right-4 cursor-pointer"
                                        >
                                            <Icon type="Scale" viewBox="0 0 19 18" width="12" height="12" class="fill-current" />
                                        </div>
                                        <img
                                            class="prevent-click rounded h-auto w-full xl:max-h-[600px]"
                                            @click="toggleFullscreen(item.attributes.image)"
                                            :src="item.attributes.image"
                                            alt="slider-image"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Image Title Click -->
                            <div class="absolute left-0 right-0 z-10 p-8 -bottom-14">
                                <div class="flex max-w-full gap-2 p-2 mx-auto overflow-x-auto bg-white rounded-md shadow-md w-fit overflow-y-clip">
                                    <span
                                        @click="[slideToImage(index)]"
                                        v-for="(item, index) in images"
                                        :key="item.id"
                                        :class="
                                            index === activeSliderIndex ? 'bg-blue text-white backgroundlightingActiv' : 'text-blue  hover:scale-105 hover:shadow-md duration-300'
                                        "
                                        class="p-2 border rounded-md shadow-md cursor-pointer border-blue whitespace-nowrap"
                                    >
                                        {{ $translate(item.attributes.title) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Andere Analysen -->
                    <div class="flex-col w-full p-4 bg-white rounded-b-xl">
                        <div class="flex flex-col gap-4 overflow-y-scroll bg-white h-min">
                            <!--h-auto-->
                            <div @click="toggleDropdown = !toggleDropdown" class="flex flex-row justify-between py-6 cursor-pointer">
                                <h2 class="text-base">Weitere Sonderberichte</h2>
                                <i :class="['fi transition-all duration-200 fi-ss-angle-up', toggleDropdown ? 'rotate-180' : 'rotate-0']"></i>
                            </div>
                            <div
                                :class="[
                                    'flex flex-col gap-3 xl:grid xl:grid-cols-2 xl:auto-rows-min xl:gap-2 transition-all duration-200 overflow-y-scroll',
                                    toggleDropdown ? 'opacity-100 h-[300px]' : 'opacity-0 h-0 xl:opacity-100'
                                ]"
                            >
                                <ContentstreamsItem v-for="contentstream in contentstreams" :contentstream="contentstream" :special="true" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contentstreams -->
                <div v-if="productGroup" class="flex flex-col p-6 bg-white rounded-xl">
                    <div class="flex flex-col gap-8 bg-white">
                        <div class="flex flex-col gap-3 overflow-y-scroll h-[500px]">
                            <ContentstreamsList
                                v-if="contentstreams.length"
                                :header="false"
                                :filterHeader="true"
                                :searchHeader="true"
                                :dateHeader="true"
                                :data="contentstreams"
                                :productGroup="productGroup"
                                :title="$t('plain_analysis_plural')"
                                :filter="'Schriftlich'"
                                class="h-[500px] overflow-hidden"
                            />
                        </div>
                    </div>
                </div>

                <!-- Content -->
                <div class="p-6 overflow-y-scroll bg-white basis-full rounded-xl">
                    <div v-if="tabContent('tab').length" ref="content">
                        <Tabs
                            :data="tabContent('tab')"
                            :activeTab="0"
                            activeColor="#353CEE"
                            :firstOnLoad="true"
                            @update:activeTab="updateActiveImageTab"
                            v-if="tabContent('tab').length > 1"
                        >
                            <template #header="{ data }">
                                <strong class="whitespace-nowrap">
                                    {{ $translate(data?.attributes.title) ? $translate(data?.attributes?.title) : $t('plain_analysis') }}
                                </strong>
                            </template>

                            <template #item="{ key, data }">
                                <div v-if="data.attributes?.wysiwyg">
                                    <div class="content fr-view" v-html="$translate(data.attributes.wysiwyg)"></div>
                                </div>
                            </template>
                        </Tabs>

                        <!-- WITHOUT Tabs -->
                        <div v-else>
                            <div v-if="tabContent('tab')[0]?.attributes?.wysiwyg">
                                <div class="content fr-view" v-html="$translate(tabContent('tab')[0].attributes.wysiwyg)"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="px-6 lg:px-20 py-5 bg-[#f1f6fa]">
            <Disclaimer />
        </div>

        <div class="sticky z-40 flex flex-col items-end gap-4 bottom-10">
            <div
                class="flex flex-row p-2 transition-all duration-200 ease-in-out bg-white rounded-md shadow-md text-blue-dark w-max"
                :class="currentScrollPercentage > 0 ? 'opacity-1 w-[76px] h-[76px] mr-2 p-2' : 'opacity-0 w-0 h-0'"
            >
                <!--Reading progress-->
                <div class="bg-blue-light rounded-md shadow-md pointer-events-none w-[60px] h-[60px]">
                    <ProgressCircle :progress="currentScrollPercentage" />
                </div>
                <!--Got to top-->
                <div
                    @click="scrollToTop"
                    class="bg-blue-light rounded-md p-2 ml-2 shadow-md transition-all ease-in-out duration-200 w-[60px] h-[60px] flex flex-col gap-2 items-center justify-center cursor-pointer"
                >
                    <font-awesome-icon :icon="['fas', 'chevron-up']" />
                </div>
            </div>
        </div>
    </Loading>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import Path from '../Path.vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Swiper, { Navigation } from 'swiper';
import { nextTick } from 'vue';
import ContentstreamsList from '../ContentstreamsList.vue';
import Disclaimer from '../../customer/Disclaimer.vue';
import ProgressCircle from '../../miscellaneous/ProgressCircle.vue';
import { formatDateByRegion, scrollToTop } from '../../../helpers';
import ContentstreamsItem from '../ContentstreamsItem.vue';
import { onScreenPercentage } from '../../../helpers';

const store = useStore();
const route = useRoute();
const router = useRouter();
const id = route.params.id;
const contentContainer = ref(null);
let userId = null;
let analysis = ref([]);
let analysisName = ref('');
let analysisContent = ref('');
let productImage = ref([]);
let productGroupShortDescription = ref('');
let contentstreams = ref([]);
let favoriteStatus = ref(false);
let content = ref(null);
let images = ref([]);
let slider = ref([]);
let activeSliderIndex = ref(0);
let productGroup = ref('');
let fullscreen = ref(false);
let fullscreenImage = ref(null);
let loading = ref(true);
let currentScrollPercentage = ref(0);
let toggleDropdown = ref(false);

Swiper.use([Navigation]);

const waitForContentLoading = (content, continueFunction = null, continueFunctions = []) => {
    const interval = setInterval(() => {
        if (store.getters['settings/isContentLoaded'](content)) {
            if (continueFunctions.length > 0) {
                waitForContentLoading(continueFunctions[0], continueFunction, continueFunctions.shift);
            } else if (continueFunction) {
                continueFunction();
            }
            clearInterval(interval);
        }
    }, 10);
};

// Get past analysis
const getPastAnalysis = async () => {
    if (store.getters['settings/isContentLoaded']('special-reports')) {
        contentstreams.value = await store.getters['contentstreams/getByKey']('special_reports').data;
    } else {
        await store
            .dispatch('contentstreams/specialReports', {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    page_size: 100,
                    page: 1,
                    key: 'special_reports'
                }
            })
            .then((response) => {
                store.commit('settings/contentLoaded', 'special-reports');
                contentstreams.value = response.data;
            });
    }
};

const toggleFavoriteReadStatus = async (contentstream, currentContentStream = false) => {
    function update() {
        const contentstreamFavorite = contentstream.read_status === null ? true : !contentstream.read_status.is_favorite;

        store.dispatch('contentstream_user/update', {
            userId: userId,
            contentstreamId: contentstream.id,
            isFavorite: contentstreamFavorite
        });

        if (currentContentStream) {
            favoriteStatus.value = !favoriteStatus.value;
        } else {
            contentstream.read_status = { is_favorite: contentstreamFavorite };
        }
    }

    if (contentstream.read_status === null) {
        initReadStatus(contentstream.id);
    }
    setTimeout(() => {
        update();
    }, 100);
};

const initReadStatus = (contentstreamID) => {
    store.dispatch('contentstream_user/init', {
        contentstreamId: contentstreamID,
        isRead: true
    });
};

const collectImages = () => {
    images.value = [];
    content.value.forEach((item) => {
        if (item?.layout === 'image' && item) {
            images.value.push(item);
        } else if (item?.layout === 'tab_wysiwyg_with_images' && item?.attributes?.images?.length > 0) {
            images.value = [...images.value, ...item.attributes.images];
        }
    });
};

const toggleFullscreen = (image) => {
    fullscreen.value = !fullscreen.value;

    // if (this.fullscreen) {
    //     // Disable scrolling on fullscreen
    //     document.body.style.overflow = 'hidden';
    //
    //     this.lastScrollPosition = window.scrollY
    //
    //     // making the content div draggable with the dragElement helper
    //     this.$dragElement(document.getElementById("content"))
    //
    //     this.$store.commit('settings/closeBackendSidebar', {name: 'backend_sidebar'})
    // } else {
    //     // Enable scrolling when exiting fullscreen
    //     document.body.style.overflow = 'auto';
    //
    //     this.$nextTick(() => {
    //         window.scrollTo({
    //             top: this.lastScrollPosition,
    //         })
    //     })
    //
    //     this.$store.commit('settings/openBackendSidebar', {name: 'backend_sidebar'})
    // }

    fullscreen.value ? (fullscreenImage.value = image) : (fullscreenImage.value = null);
};

const slideToImage = (index) => {
    if (Object.keys(slider.value).length > 0) {
        slider.value.slideTo(index);
        fullscreenImage.value = images.value[index].attributes.image;
    }
    activeSliderIndex.value = index;
};

const initSlider = () => {
    slider.value = new Swiper('.image-slider', {
        slidesPerView: 1,
        imageSize: 'cover'
    });
};

const createPreviousURL = () => {
    if (productGroup.value.productType === 'doubleProduct') {
        return `/dashboard/markets-double/${productGroup.value.id}`;
    }

    if (productGroup.value.productType === 'bundleProduct') {
        return `/dashboard/markets-package/${productGroup.value.id}`;
    }

    return `/dashboard/markets/${productGroup.value.id}`;
};

const tabContent = (layout) => {
    if (analysis.value.content === undefined) return [];

    return analysis.value?.content.filter((item) => {
        if (layout === 'tab') {
            return item.layout === 'tab_wysiwyg' || item.layout === 'tab_wysiwyg_with_images';
        } else if (layout === 'image' && item?.attributes?.images?.length > 0) {
            return true;
        } else {
            return item.layout === layout;
        }
    });
};

const updateActiveImageTab = (event) => {
    if (typeof event.attributes.images === 'object') {
        let first_image_index = images.value.findIndex((image) => {
            return image.key === event?.attributes?.images[0]?.key;
        });

        if (first_image_index !== undefined && first_image_index !== -1) {
            slideToImage(first_image_index);
        }
    }
};

const getContentstreamData = (contentstreamId) => {
    store.dispatch('contentstream/show', contentstreamId).then(() => {
        analysis.value = store.getters['contentstream/get'];
        // Verwende Platzhalter oder Standardwerte, wenn Produktinformationen fehlen
        productImage.value = analysis.value.product?.image || null;
        analysisName.value = analysis.value.product?.name.de || analysis.value.title.de;
        analysisContent.value = analysis.value.content?.[0]?.attributes?.wysiwyg || 'No content available';
        productGroupShortDescription.value = analysis.value.product?.productGroups?.data?.[0]?.short_description || 'No description available';
        content.value = analysis.value.content || [];
        productGroup.value = analysis.value.product?.productGroups?.data?.[0] || null;

        collectImages();

        waitForContentLoading('contentstreams', () => {
            getPastAnalysis();
        });

        nextTick(() => {
            initSlider();
        });
        loading.value = false;
    });
};

const scrollPercentage = () => {
    currentScrollPercentage.value = onScreenPercentage(contentContainer.value);
};

onMounted(() => {
    store.dispatch('user/user').then(() => {
        userId = store.getters['user/get'].id;
    });

    getContentstreamData(id);

    window.addEventListener('scroll', scrollPercentage);
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', scrollPercentage);
});

router.afterEach((to, from) => {
    if (to.params.id !== from.params.id && to.name === from.name) {
        loading.value = true;
        getContentstreamData(to.params.id);
    }
});
</script>
<style>
.backgroundlightingActiv {
    position: relative;
    transform: scale(1.05);
}

.backgroundlightingActiv:before,
.backgroundlightingActiv:after {
    content: '';
    position: absolute;
    left: 0px;
    top: 0px;
    background: linear-gradient(45deg, #031837, #363cd2, #031837);
    background-size: 400%;
    width: 100%;
    height: 100%;
    z-index: -1;
    animation: steam 20s linear infinite;
    opacity: 1;
    transition: all 1.5s ease;
}

@keyframes steam {
    0% {
        background-position: 0 0;
    }
    50% {
        background-position: 400% 0;
    }
    100% {
        background-position: 0 0;
    }
}

.backgroundlightingActiv:after {
}
</style>
